/**
 * @ipc/validation · src/schemas/merchants.ts — группа `/admin/merchants`
 * (раздел 13, work item 10).
 *
 * Мерчант в IPC = внутренний сервис/продукт тенанта для разреза платежей
 * (аналитика, RBAC scope, ссылки). Multicard application_id/secret/store_id
 * общие на тенант (таблица Tenant) — на create/update полей провайдера
 * обычно нет: backend подставляет tenant multicard. Опциональные
 * applicationId/secret/storeId оставлены для редкого override через API.
 */
import { z } from 'zod/v4';
import {
  paginationSchema,
  searchSchema,
  sortOrderSchema,
  booleanQuerySchema,
  paymentProviderSchema,
} from '../common/primitives.ts';
import { paymentMethodListSchema } from '../enums/payment-methods.ts';

export const createMerchantSchema = z.object({
  /** Включённые способы оплаты; не указаны — только страница Multicard. */
  paymentMethods: paymentMethodListSchema.optional(),
  slug: z.string().min(1).max(100),
  displayName: z.string().min(1).max(255),
  /** Override Multicard application_id; иначе — с Tenant.multicard */
  applicationId: z.string().min(1).optional(),
  /** Override secret; иначе — с Tenant.multicard (шифруется в контроллере) */
  secret: z.string().min(1).optional(),
  /** Override store_id; иначе — с Tenant.multicard */
  storeId: z.string().min(1).optional(),
  taxRegistration: z.number().int().min(0).max(3).default(0),
  taxMxik: z.string().optional(),
  taxPackageCode: z.string().optional(),
  defaultCallbackUrl: z.string().url().optional(),
  defaultReturnUrl: z.string().url().optional(),
});
export type CreateMerchantDto = z.infer<typeof createMerchantSchema>;

export const updateMerchantSchema = createMerchantSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateMerchantDto = z.infer<typeof updateMerchantSchema>;

export const merchantIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});
export type MerchantIdParamDto = z.infer<typeof merchantIdParamSchema>;

/** Колонки серверной сортировки списка мерчантов (GET /admin/merchants). */
export const MERCHANT_SORT_COLUMNS = ['createdAt', 'displayName', 'slug'] as const;
export const merchantSortBySchema = z.enum(MERCHANT_SORT_COLUMNS);
export type MerchantSortBy = z.infer<typeof merchantSortBySchema>;

export const merchantFiltersSchema = paginationSchema.extend({
  search: searchSchema,
  isActive: booleanQuerySchema,
  provider: paymentProviderSchema.optional(),
  sortBy: merchantSortBySchema.default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type MerchantFiltersDto = z.infer<typeof merchantFiltersSchema>;
